import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { GALLERY } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const variants = {
    enter: (dir) => ({ x: dir > 0 ? 90 : -90, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -90 : 90, opacity: 0 }),
};

export default function Gallery() {
    const [[index, direction], setIndex] = useState([0, 0]);
    const [open, setOpen] = useState(false);
    const photo = GALLERY[index];

    const go = (dir) =>
        setIndex(([i]) => [
            (i + dir + GALLERY.length) % GALLERY.length,
            dir,
        ]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
            if (e.key === "ArrowRight") go(1);
            if (e.key === "ArrowLeft") go(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    useEffect(() => {
        const lenis = window.__lenis;
        if (open) {
            lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            lenis?.start();
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const arrow =
        "icon-btn size-11 shrink-0 shadow-md hover:shadow-lg sm:size-12 !bg-white";

    return (
        <section
            id="gallery"
            data-testid="gallery-section"
            className="border-y border-blush/25 bg-cream-deep/60 py-20 sm:py-28"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6">
                <div className="text-center">
                    <p className="eyebrow">Fresh from the kitchen</p>
                    <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-cocoa sm:text-5xl">
                        recent bakes
                    </h2>
                </div>

                <div className="mx-auto mt-10 flex max-w-md items-center justify-center gap-3 sm:mt-14 sm:gap-6">
                    <button
                        type="button"
                        data-testid="gallery-prev-btn"
                        onClick={() => go(-1)}
                        aria-label="Previous cake"
                        className={arrow}
                    >
                        <ChevronLeft className="size-5" />
                    </button>

                    <button
                        type="button"
                        data-testid="gallery-photo"
                        onClick={() => setOpen(true)}
                        aria-label="View this cake full screen"
                        className="relative block aspect-[3/4] w-full max-w-[340px] cursor-zoom-in overflow-hidden rounded-t-full rounded-b-[2rem] bg-white shadow-[0_30px_70px_-30px_rgba(44,34,30,0.4)] ring-1 ring-blush/50 sm:max-w-[380px]"
                    >
                        <AnimatePresence custom={direction} mode="popLayout" initial={false}>
                            <motion.img
                                key={index}
                                src={photo.src}
                                alt={photo.alt}
                                custom={direction}
                                variants={variants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.45, ease: EASE }}
                                className="absolute inset-0 size-full object-cover"
                            />
                        </AnimatePresence>
                        <span className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-cocoa shadow-sm backdrop-blur-sm">
                            <Expand className="mr-1 inline size-3" />
                            tap to view
                        </span>
                    </button>

                    <button
                        type="button"
                        data-testid="gallery-next-btn"
                        onClick={() => go(1)}
                        aria-label="Next cake"
                        className={arrow}
                    >
                        <ChevronRight className="size-5" />
                    </button>
                </div>

                <div className="mt-8 text-center">
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.p
                            key={index}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.3 }}
                            className="font-serif text-xl italic text-cocoa sm:text-2xl"
                            data-testid="gallery-caption"
                        >
                            {photo.tag}
                        </motion.p>
                    </AnimatePresence>
                    <p
                        className="mt-2 text-xs font-semibold tracking-[0.25em] text-cocoa-soft"
                        data-testid="gallery-counter"
                    >
                        {String(index + 1).padStart(2, "0")} /{" "}
                        {String(GALLERY.length).padStart(2, "0")}
                    </p>
                    <div className="mt-5 flex justify-center gap-2">
                        {GALLERY.map((g, i) => (
                            <button
                                key={g.src}
                                type="button"
                                data-testid={`gallery-dot-${i}`}
                                aria-label={`Go to photo ${i + 1}`}
                                onClick={() => setIndex([i, i > index ? 1 : -1])}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    i === index ? "w-7 bg-leaf" : "w-2 bg-blush hover:bg-blush-deep"
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="gallery-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => setOpen(false)}
                        className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-cocoa/95 p-4 backdrop-blur-sm"
                    >
                        <button
                            type="button"
                            data-testid="lightbox-close-btn"
                            aria-label="Close full screen view"
                            onClick={() => setOpen(false)}
                            className="absolute right-4 top-4 z-10 inline-flex size-11 items-center justify-center rounded-full border border-cream/30 text-cream transition hover:bg-cream hover:text-cocoa"
                        >
                            <X className="size-5" />
                        </button>

                        <button
                            type="button"
                            data-testid="lightbox-prev-btn"
                            aria-label="Previous cake"
                            onClick={(e) => {
                                e.stopPropagation();
                                go(-1);
                            }}
                            className="absolute left-3 top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-cream/30 text-cream transition hover:bg-cream hover:text-cocoa sm:left-6"
                        >
                            <ChevronLeft className="size-5" />
                        </button>

                        <motion.img
                            key={`lb-${index}`}
                            src={photo.src}
                            alt={photo.alt}
                            initial={{ scale: 0.94, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.35, ease: EASE }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[80vh] max-w-full rounded-2xl object-contain shadow-2xl"
                        />

                        <div
                            className="mt-4 text-center text-cream"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <p className="font-serif text-lg italic">{photo.tag}</p>
                            <p className="mt-1 text-xs tracking-[0.25em] text-cream/60">
                                {String(index + 1).padStart(2, "0")} /{" "}
                                {String(GALLERY.length).padStart(2, "0")}
                            </p>
                        </div>

                        <button
                            type="button"
                            data-testid="lightbox-next-btn"
                            aria-label="Next cake"
                            onClick={(e) => {
                                e.stopPropagation();
                                go(1);
                            }}
                            className="absolute right-3 top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-cream/30 text-cream transition hover:bg-cream hover:text-cocoa sm:right-6"
                        >
                            <ChevronRight className="size-5" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
